import React, { useState, useRef } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import SafeMarkdown from '../../Common/SafeMarkdown';
import { PassageIllustration as PassageIllustrationType } from './assessmentsTypes';

interface PassageIllustrationsProps {
  illustrations: PassageIllustrationType[] | undefined;
  passageText: string;
  passageTitle?: string;
  genre?: string;
}

/**
 * Renders a passage with inline AI-generated illustrations.
 *
 * Images are placed at paragraph indices specified by the placementIndex
 * field in each illustration. The component splits the passage by paragraphs
 * and inserts images after the appropriate paragraphs.
 */
export const PassageIllustrations: React.FC<PassageIllustrationsProps> = ({
  illustrations,
  passageText,
  passageTitle,
  genre
}) => {
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const imageRefs = useRef<Record<number, HTMLImageElement>>({});

  // Split passage into paragraphs (double newline indicates paragraph break)
  const paragraphs = React.useMemo(() => {
    return passageText.split(/\n\n+/).map(p => p.trim()).filter(p => p.length > 0);
  }, [passageText]);

  // Track which images are at which paragraph indices
  const imagesByParagraph = React.useMemo(() => {
    const map = new Map<number, PassageIllustrationType[]>();
    if (illustrations) {
      illustrations.forEach((img) => {
        if (img.placementIndex !== undefined && img.imageGenerated && img.imageUrl) {
          if (!map.has(img.placementIndex)) {
            map.set(img.placementIndex, []);
          }
          map.get(img.placementIndex)!.push(img);
        }
      });
    }
    return map;
  }, [illustrations]);

  // Handle image load success
  const handleImageLoad = (imgIndex: number) => {
    setLoadedImages(prev => new Set(prev).add(imgIndex));
  };

  // Handle image load error
  const handleImageError = (imgIndex: number) => {
    setFailedImages(prev => new Set(prev).add(imgIndex));
  };

  // Get image styles - slightly smaller than default (80% of visualSize, capped at 80% max)
  const getImageStyles = (alignment: string, visualSize: number) => {
    const sizeMultiplier = Math.min(visualSize * 0.8, 80) / 100; // 80% of visualSize, max 80%

    switch (alignment) {
      case 'left':
        return {
          float: 'left' as const,
          width: `calc(${50 * sizeMultiplier}% - 20px)`,
          marginRight: '20px',
          marginBottom: '10px',
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'block' as const,
        };
      case 'right':
        return {
          float: 'right' as const,
          width: `calc(${50 * sizeMultiplier}% - 20px)`,
          marginLeft: '20px',
          marginBottom: '10px',
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'block' as const,
        };
      case 'full-width':
        return {
          width: '100%',
          marginBottom: '20px',
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'block' as const,
        };
      case 'center':
      default:
        return {
          width: `${80 * sizeMultiplier}%`,
          margin: '16px auto',
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'block' as const,
        };
    }
  };

  // If no illustrations or passage, return null
  if (!illustrations || illustrations.length === 0 || !passageText) {
    return null;
  }

  // Check if any images were actually generated
  const hasGeneratedImages = illustrations.some(img => img.imageGenerated && img.imageUrl);
  if (!hasGeneratedImages) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* Display title if provided */}
      {passageTitle && (
        <Box component="h2" sx={{ mb: 3, fontWeight: 'bold', color: '#1a202c', fontSize: '1.75rem' }}>
          {passageTitle}
        </Box>
      )}

      {paragraphs.map((paragraph, paraIdx) => {
        const imagesForThisParagraph = imagesByParagraph.get(paraIdx) || [];

        return (
          <React.Fragment key={`para-${paraIdx}`}>
            {/* Paragraph text with markdown support */}
            <Box sx={{ mb: imagesForThisParagraph.length > 0 ? 1 : 2 }}>
              <SafeMarkdown>{paragraph}</SafeMarkdown>
            </Box>

            {/* Images for this paragraph */}
            {imagesForThisParagraph.map((img, imgIdx) => {
              const uniqueImgIndex = illustrations.indexOf(img);
              const isLoaded = loadedImages.has(uniqueImgIndex);
              const isFailed = failedImages.has(uniqueImgIndex);

              if (!img.imageUrl) {
                return null;
              }

              const imageStyles = getImageStyles(img.alignment || 'center', img.visualSize || 100);

              return (
                <Box
                  key={`img-${uniqueImgIndex}`}
                  sx={{
                    my: 1.5,
                    ...(img.alignment === 'left' || img.alignment === 'right' ? {} : { display: 'flex', justifyContent: 'center' })
                  }}
                >
                  {/* Loading indicator */}
                  {!isLoaded && !isFailed && (
                    <CircularProgress size={32} sx={{ display: 'block', mx: 'auto', mb: 1 }} />
                  )}

                  {/* Image */}
                  <img
                    ref={el => { if (el) imageRefs.current[uniqueImgIndex] = el; }}
                    src={img.imageUrl}
                    alt={img.altText || `Illustration for paragraph ${paraIdx + 1}`}
                    onLoad={() => handleImageLoad(uniqueImgIndex)}
                    onError={() => handleImageError(uniqueImgIndex)}
                    style={{
                      ...imageStyles,
                      display: isLoaded ? imageStyles.display : 'none',
                    }}
                  />

                  {/* Error message */}
                  {isFailed && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Failed to load illustration.
                    </Alert>
                  )}
                </Box>
              );
            })}

            {/* Clearfix for floated images */}
            {imagesForThisParagraph.some(img => img.alignment === 'left' || img.alignment === 'right') && (
              <Box sx={{ clear: 'both', height: 0 }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default PassageIllustrations;
